import { useMutation, useQuery } from "@apollo/client/react";
import { InboxIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";

import { ErrorFallback, Head } from "../../../../components/core";
import { Button } from "../../../../components/form";
import { LimitBy, TableCursor } from "../../../../components/table";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import {
  GET_PRICINGCATEGORIES,
  REMOVE_PRICINGCATEGORY,
} from "../../../../graphql/inventory/pricing/pricing-categories";
import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import { formatDate } from "../../../../utils";
import { AuthShield } from "../../../auth/core";

type PricingMethodField = {
  fieldType: string;
  fieldKey: string;
  fieldName: string;
  fieldOptions: string[];
};

type PricingMethod = {
  id?: string;
  name: string;
  sellPriceFormula: string[];
  pricingMethodFields: PricingMethodField[];
  createdAt: string;
  status: boolean;
};

type PricingStructure = {
  id: string;
  name: string;
  pricingMethod: PricingMethod;
  customerType: number;
};

type PricingCategory = {
  id: string;
  name: string;
  description: string;
  pricingStructures: PricingStructure[];
  createdAt: string;
  status: boolean;
};

const PricingCategoryList = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const { addNotify } = useNotifyContext();
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);

  const { data, loading, error, refetch } = useQuery<{
    fetchPricingCategories: PricingCategory[];
  }>(GET_PRICINGCATEGORIES);

  const pricingCategories = useMemo<PricingCategory[]>(
    () =>
      data?.fetchPricingCategories.map((item: PricingCategory) => ({
        ...item,
        createdAt: formatDate(item.createdAt),
      })) || [],
    [data]
  );

  const [deletePricingCategory] = useMutation(REMOVE_PRICINGCATEGORY, {
    refetchQueries: [
      {
        query: GET_PRICINGCATEGORIES,
      },
    ],
  });
  const handleDelete = async (id: string) => {
    return await deletePricingCategory({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.pricingCategoryDelete) {
          const deletedPricingCategory =
            data?.pricingCategoryDelete?.pricingCategory;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `Pricing category ${deletedPricingCategory.name} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Pricing category delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Pricing category delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<PricingCategory, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.name,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <div className="flex space-x-2 md:space-x-4">
            <AuthShield access={["update-pricings"]}>
              <Button
                type="link"
                href={`/inventory/pricing/pricing-categories/${props.row.original.id}`}
                variant="icon"
                className="text-blue-500 hover:text-blue-600"
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">Edit, {props.row.original.name}</span>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-pricings"]}>
              <Button
                variant="icon"
                className="text-red-500 hover:text-red-600"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id,
                    name: props.row.original.name,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.name}
                </span>
              </Button>
            </AuthShield>
          </div>
        ),
      },
    ],
    []
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={PricingCategoryListResource.name}
        heading={PricingCategoryListResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Pricing",
            href: "/inventory/pricing",
          },
          {
            name: PricingCategoryListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {PricingCategoryListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {PricingCategoryListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-pricings"]}>
              <Button
                type="link"
                href="/inventory/pricing/pricing-categories/add"
              >
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add pricing category
              </Button>
            </AuthShield>
          </div>
        </div>

        <TableCursor
          data={pricingCategories}
          columns={columns}
          loading={loading}
          totalRows={pricingCategories.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No pricing categories found"
          fallbackText="You can add a new pricing category by clicking on add pricing category button."
        />
      </div>
    </>
  );
};

export default PricingCategoryList;
export const PricingCategoryListResource: ResourceProps = {
  name: "Pricing Categories",
  description: "A list of pricing categories",
  access: ["read-pricings"],
  path: "pricing-categories",
};
